class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.abortController.abort();
    this.abortController = new AbortController();
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    if (searchForTextElement) searchForTextElement.innerText = `View all in-stock results for “${newTerm}”`;
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  escapeHTML(value) {
    const characters = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    };

    return String(value ?? '').replace(/[&<>"']/g, (character) => characters[character]);
  }

  getSizedImageUrl(imageUrl) {
    if (!imageUrl) return '';
    return `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}width=360`;
  }

  formatMoney(value) {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '';

    const currency = window.Shopify?.currency?.active || 'GBP';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency,
      }).format(amount);
    } catch (error) {
      return `£${amount.toFixed(2)}`;
    }
  }

  getSizeMarkup(product) {
    const sizes = [
      ...new Set(
        (product.variants || [])
          .filter((variant) => variant.available)
          .map((variant) => String(variant.title || '').split(' / ')[0].trim())
          .filter((size) => size && size.toLowerCase() !== 'default title')
      ),
    ];

    if (!sizes.length) return '';

    const visibleSizes = sizes.slice(0, 4);
    const extraSizes = sizes.length - visibleSizes.length;
    const label = sizes.length === 1 ? 'Size' : 'Sizes';
    const suffix = extraSizes > 0 ? ` +${extraSizes}` : '';

    return `<p class="predictive-search__sizes"><span>${label}</span> ${this.escapeHTML(
      visibleSizes.join(' · ')
    )}${suffix}</p>`;
  }

  buildSearchResultsMarkup(products, searchTerm) {
    const availableProducts = (products || []).filter((product) => product.available).slice(0, 6);
    const escapedTerm = this.escapeHTML(searchTerm);

    const productCards = availableProducts
      .map((product, index) => {
        const title = this.escapeHTML(product.title);
        const url = this.escapeHTML(product.url);
        const sourceImage = product.featured_image?.url || product.image || '';
        const imageUrl = this.escapeHTML(this.getSizedImageUrl(sourceImage));
        const imageAlt = this.escapeHTML(product.featured_image?.alt || product.title);
        const price = this.escapeHTML(this.formatMoney(product.price_min ?? product.price));
        const imageMarkup = imageUrl
          ? `<img class="predictive-search__image" src="${imageUrl}" alt="${imageAlt}" width="360" height="360" loading="lazy">`
          : '<div class="predictive-search__image predictive-search__image--empty" aria-hidden="true"></div>';

        return `
          <li
            id="predictive-search-option-product-${index + 1}"
            class="predictive-search__list-item"
            role="option"
            aria-selected="false"
          >
            <a href="${url}" class="predictive-search__item link link--text" tabindex="-1">
              <div class="predictive-search__media">${imageMarkup}</div>
              <div class="predictive-search__item-content">
                <p class="predictive-search__item-heading">${title}</p>
                ${this.getSizeMarkup(product)}
                ${price ? `<div class="price predictive-search__price">${price}</div>` : ''}
              </div>
            </a>
          </li>`;
      })
      .join('');

    const resultsContent = availableProducts.length
      ? `<ul
          id="predictive-search-results-products-list"
          class="predictive-search__results-list list-unstyled"
          role="group"
          aria-labelledby="predictive-search-products"
        >${productCards}</ul>`
      : `<div class="predictive-search__empty">
          <p class="predictive-search__empty-title">No in-stock products found</p>
          <p>Try a brand, garment type, colour or size.</p>
        </div>`;

    const countMarkup = availableProducts.length
      ? `<span class="predictive-search__count">${availableProducts.length} quick result${
          availableProducts.length === 1 ? '' : 's'
        }</span>`
      : '';

    const liveRegionText = availableProducts.length
      ? `${availableProducts.length} in-stock product${availableProducts.length === 1 ? '' : 's'} found`
      : `No in-stock products found for ${escapedTerm}`;

    return `
      <div id="predictive-search-results" role="listbox">
        <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper">
          <div class="predictive-search__topline">
            <div>
              <span class="predictive-search__eyebrow">Available now</span>
              <h2 id="predictive-search-products" class="predictive-search__heading">In-stock products</h2>
            </div>
            ${countMarkup}
          </div>
          ${resultsContent}
        </div>
        <div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">
          <button
            type="submit"
            class="predictive-search__item predictive-search__item--term link link--text"
            tabindex="-1"
            role="option"
            aria-selected="false"
          >
            <span data-predictive-search-search-for-text>View all in-stock results for “${escapedTerm}”</span>
            <svg class="icon icon-arrow" aria-hidden="true" viewBox="0 0 14 10" fill="none">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M8.537.808a.5.5 0 0 1 .817-.162l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 1 1-.708-.708L11.793 5.5H1a.5.5 0 0 1 0-1h10.793L8.646 1.354a.5.5 0 0 1-.109-.546Z" fill="currentColor"/>
            </svg>
          </button>
        </div>
      </div>
      <span class="hidden" data-predictive-search-live-region-count-value>${liveRegionText}</span>`;
  }

  buildSearchErrorMarkup(searchTerm) {
    const escapedTerm = this.escapeHTML(searchTerm);

    return `
      <div id="predictive-search-results" role="listbox">
        <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper">
          <div class="predictive-search__empty">
            <p class="predictive-search__empty-title">Quick results could not load</p>
            <p>You can still search the full collection.</p>
          </div>
        </div>
        <div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">
          <button
            type="submit"
            class="predictive-search__item predictive-search__item--term link link--text"
            tabindex="-1"
            role="option"
            aria-selected="false"
          >
            <span data-predictive-search-search-for-text>Search the collection for “${escapedTerm}”</span>
            <svg class="icon icon-arrow" aria-hidden="true" viewBox="0 0 14 10" fill="none">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M8.537.808a.5.5 0 0 1 .817-.162l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 1 1-.708-.708L11.793 5.5H1a.5.5 0 0 1 0-1h10.793L8.646 1.354a.5.5 0 0 1-.109-.546Z" fill="currentColor"/>
            </svg>
          </button>
        </div>
      </div>
      <span class="hidden" data-predictive-search-live-region-count-value>Quick results could not load for ${escapedTerm}</span>`;
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, ' ').trim().toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const searchParameters = new URLSearchParams({
      q: searchTerm,
      'resources[type]': 'product',
      'resources[limit]': '6',
      'resources[limit_scope]': 'all',
      'resources[options][unavailable_products]': 'hide',
      'resources[options][fields]': 'title,product_type,variants.title,vendor',
    });

    const predictiveSearchUrl = routes.predictive_search_url.endsWith('.json')
      ? routes.predictive_search_url
      : `${routes.predictive_search_url}.json`;

    fetch(`${predictiveSearchUrl}?${searchParameters.toString()}`, {
      signal: this.abortController.signal,
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) {
          this.close();
          throw new Error(`Predictive search failed with status ${response.status}.`);
        }

        return response.json();
      })
      .then((response) => {
        const products = response?.resources?.results?.products || [];
        const resultsMarkup = this.buildSearchResultsMarkup(products, searchTerm);
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) {
          return;
        }
        this.renderSearchResults(this.buildSearchErrorMarkup(searchTerm));
        console.error(error);
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
